import { canonicalJson } from './pack-engine';

/**
 * These are the four statutory content elements for an NDB statement. The
 * citations deliberately remain unverified until counsel has checked primary
 * material; the pack must not imply that Statura has made that verification.
 */
export const NDB_STATEMENT_REQUIRED_ELEMENTS = [
  {
    key: 'entity_identity_and_contact',
    heading: 'Entity identity and contact',
    citation: 'Privacy Act 1988 (Cth) s 26WK(3)(a) (UNVERIFIED)',
    unverified: true,
  },
  {
    key: 'breach_description',
    heading: 'Description of the breach',
    citation: 'Privacy Act 1988 (Cth) s 26WK(3)(b) (UNVERIFIED)',
    unverified: true,
  },
  {
    key: 'information_kinds',
    heading: 'Kinds of information concerned',
    citation: 'Privacy Act 1988 (Cth) s 26WK(3)(c) (UNVERIFIED)',
    unverified: true,
  },
  {
    key: 'recommended_steps',
    heading: 'Recommended steps for individuals',
    citation: 'Privacy Act 1988 (Cth) s 26WK(3)(d) (UNVERIFIED)',
    unverified: true,
  },
] as const;

export type NdbNotificationPath = 'EACH_INDIVIDUAL' | 'AT_RISK_ONLY' | 'PUBLISH';
export type NdbStatementElementKey = (typeof NDB_STATEMENT_REQUIRED_ELEMENTS)[number]['key'];

export interface BuildNdbStatementPackInput {
  breachId: string;
  version: number;
  entity: { name: string; contact: string };
  breachDescription: string;
  occurredFrom?: string | null;
  occurredTo?: string | null;
  awareOfSuspicionAt?: string | null;
  detectedAt?: string | null;
  containmentSummary?: string | null;
  otherEntityDetails?: string | null;
  individualsAffected?: number | null;
  informationKinds: readonly string[];
  recommendedSteps: readonly string[];
  notificationPath: NdbNotificationPath;
  notificationPathReason: string;
  templateVersion: string;
  legalSourceVersion: string;
  sha256Hex: (canonical: string) => string;
}

export interface NdbStatementPack {
  kind: 'NDB_STATEMENT';
  manifest: {
    breachId: string;
    version: number;
    templateVersion: string;
    legalSourceVersion: string;
    notificationPath: NdbNotificationPath;
    notificationPathReason: string;
    facts: {
      occurredFrom: string | null;
      occurredTo: string | null;
      awareOfSuspicionAt: string | null;
      detectedAt: string | null;
      containmentSummary: string | null;
      otherEntityDetails: string | null;
      individualsAffected: number | null;
    };
    legalCitation: 'Privacy Act 1988 (Cth) ss 26WK, 26WL (UNVERIFIED)';
  };
  elements: readonly {
    key: NdbStatementElementKey;
    heading: string;
    citation: string;
    unverified: boolean;
    value: unknown;
  }[];
  contentDigest: string;
}

const compare = (left: string, right: string): number => (left < right ? -1 : left > right ? 1 : 0);

/** Builds a deterministic, no-individual-data statement snapshot for human lodgement. */
export function buildNdbStatementPack(input: BuildNdbStatementPackInput): NdbStatementPack {
  if (!input.breachId || !Number.isInteger(input.version) || input.version < 1)
    throw new Error('ndb_statement_scope_required');
  if (
    !input.entity.name ||
    !input.entity.contact ||
    !input.breachDescription ||
    !input.notificationPathReason
  )
    throw new Error('ndb_statement_content_required');
  if (!['EACH_INDIVIDUAL', 'AT_RISK_ONLY', 'PUBLISH'].includes(input.notificationPath))
    throw new Error('ndb_notification_path_invalid');
  const informationKinds = [
    ...new Set(input.informationKinds.map((value) => value.trim()).filter(Boolean)),
  ].sort(compare);
  const recommendedSteps = [
    ...new Set(input.recommendedSteps.map((value) => value.trim()).filter(Boolean)),
  ].sort(compare);
  if (!informationKinds.length || !recommendedSteps.length)
    throw new Error('ndb_statement_content_required');
  const values: Record<NdbStatementElementKey, unknown> = {
    entity_identity_and_contact: {
      name: input.entity.name.trim(),
      contact: input.entity.contact.trim(),
    },
    breach_description: input.breachDescription.trim(),
    information_kinds: informationKinds,
    recommended_steps: recommendedSteps,
  };
  const draft: Omit<NdbStatementPack, 'contentDigest'> = {
    kind: 'NDB_STATEMENT',
    manifest: {
      breachId: input.breachId,
      version: input.version,
      templateVersion: input.templateVersion,
      legalSourceVersion: input.legalSourceVersion,
      notificationPath: input.notificationPath,
      notificationPathReason: input.notificationPathReason.trim(),
      facts: {
        occurredFrom: input.occurredFrom ?? null,
        occurredTo: input.occurredTo ?? null,
        awareOfSuspicionAt: input.awareOfSuspicionAt ?? null,
        detectedAt: input.detectedAt ?? null,
        containmentSummary: input.containmentSummary?.trim() || null,
        otherEntityDetails: input.otherEntityDetails?.trim() || null,
        individualsAffected: input.individualsAffected ?? null,
      },
      legalCitation: 'Privacy Act 1988 (Cth) ss 26WK, 26WL (UNVERIFIED)',
    },
    elements: NDB_STATEMENT_REQUIRED_ELEMENTS.map((definition) => ({
      ...definition,
      value: values[definition.key],
    })),
  };
  return { ...draft, contentDigest: input.sha256Hex(canonicalJson(draft)) };
}
